import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { Recipient } from '@adornis/mails/db/recipients.js';
import { Message } from '@adornis/mails/server/communication.js';
import { AdornisMailer } from '@adornis/mails/server/mailer.js';
import { validate } from '@adornis/validation/decorators.js';
import { email } from '@adornis/validation/functions/email.js';
import { html } from 'lit';

@Entity()
export class ReleaseNotification extends MongoEntity {
  static override _class = 'ReleaseNotification';
  static override _collectionName = 'release-notifications';

  @Field(type => String)
  @validate(email())
  email!: string;

  @Field(type => Date, { default: v => v ?? new Date() })
  signupDate: Maybe<Date>;
}

export const registerReleaseNotification = registerMutation({
  type: () => Boolean,
  params: [
    {
      name: 'email',
      type: () => ReleaseNotification,
    },
  ],
  operationName: 'registerReleaseNotification',
  resolve: (email: ReleaseNotification) => async () => {
    // Check if email already exists to prevent duplicates
    const existingNotification = await (await getCollection(ReleaseNotification._class)).findOne({ email });
    if (existingNotification) {
      return false; // Email already registered
    }

    await email.create();

    // Send internal notification email
    const mailer = AdornisMailer.getInstance();
    await mailer.sendMail(
      await Message.compose({
        subject: 'New Release Notification Signup',
        html: html`<p>A new user has signed up for release notifications:</p>
          <ul>
            <li><strong>Email:</strong> ${email.email}</li>
            <li><strong>Signup Date:</strong> ${email.signupDate?.toISOString()}</li>
          </ul>`,
      }),
      new Recipient('test@adornis.de'),
    );

    // Send email notification
    await mailer.sendMail(
      await Message.compose({
        subject: 'Thank you for signing up for release notifications',
        html: html`<p>Thank you for signing up for release notifications!</p>
          <p>Stay tuned for updates on new features and releases.</p>`,
      }),
      new Recipient(email.email),
    );

    return true;
  },
});
